import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDownRight, Minus, Clock, DollarSign, Users, Wand2,
  RotateCcw, Loader2, Play, SlidersHorizontal, Target, ChevronRight,
  Bookmark,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import SaveScenarioDialog, { SavedScenariosCard } from '../components/SaveScenarioDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtHours = (v) => `${Math.round((v || 0) * 10) / 10} hrs`;
const fmtCurrency = (v, sym = '$') => `${sym}${Math.round(v || 0).toLocaleString()}`;
const fmtWeeks = (v) => `${v ?? 0} wk`;
const fmtFte = (v) => `${((v || 0)).toFixed(2)}`;

function DeltaPill({ delta, format, invert = false, sym = '$' }) {
  if (delta === 0 || delta === null || delta === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="delta-pill-zero">
        <Minus className="w-3 h-3" /> No change
      </span>
    );
  }
  const positive = invert ? delta < 0 : delta > 0;
  const color = positive ? 'text-red-600 bg-red-50' : 'text-emerald-700 bg-emerald-50';
  const Icon = delta > 0 ? ArrowUpRight : ArrowDownRight;
  let formatted;
  switch (format) {
    case 'currency':
      formatted = `${sym}${Math.round(Math.abs(delta)).toLocaleString()}`; break;
    case 'hours':
      formatted = `${Math.abs(Math.round(delta * 10) / 10)} hrs`; break;
    case 'weeks':
      formatted = `${Math.abs(delta)} wk`; break;
    case 'fte':
      formatted = `${Math.abs(delta).toFixed(2)} FTE`; break;
    default:
      formatted = `${Math.abs(delta)}`;
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {delta > 0 ? '+' : '-'}{formatted}
    </span>
  );
}

function KPICard({ icon: Icon, label, baseline, whatif, format, invert = false, testid, sym = '$' }) {
  const delta = (whatif ?? 0) - (baseline ?? 0);
  const formatters = {
    hours: fmtHours, currency: (v) => fmtCurrency(v, sym), weeks: fmtWeeks, fte: fmtFte,
  };
  const f = formatters[format] || ((v) => String(v));
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide mb-2">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-2xl font-semibold text-slate-900">{f(whatif)}</div>
          <DeltaPill delta={delta} format={format} invert={invert} sym={sym} />
        </div>
        <div className="text-[11px] text-slate-500 mt-1">Baseline {f(baseline)}</div>
      </CardContent>
    </Card>
  );
}

function ComponentEffortEditor({ components, overrides, onChange, onReset }) {
  const grouped = useMemo(() => {
    const g = {};
    components.forEach((c) => {
      const key = c.group || 'Other';
      if (!g[key]) g[key] = [];
      g[key].push(c);
    });
    return g;
  }, [components]);

  const stepValue = (name, baseline, step) => {
    const current = overrides[name] !== '' && overrides[name] != null
      ? Number(overrides[name])
      : (baseline || 0);
    const next = Math.max(0, Math.round((current + step) * 10) / 10);
    onChange(name, String(next));
  };

  return (
    <div className="space-y-4" data-testid="component-effort-editor">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-600">
          Type an absolute hour value, or use ± to step. Empty = keep baseline.
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          data-testid="component-overrides-reset"
          className="h-7 text-xs"
        >
          <RotateCcw className="w-3 h-3 mr-1" /> Reset all
        </Button>
      </div>
      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="border border-slate-200 rounded-md">
          <div className="px-3 py-2 bg-slate-50 text-xs font-medium text-slate-700 border-b border-slate-200">
            {group} <span className="text-slate-400 font-normal">({items.length})</span>
          </div>
          <div className="divide-y divide-slate-100">
            {items.map((c) => {
              const override = overrides[c.name];
              const hasOverride = override !== undefined && override !== '';
              const delta = hasOverride ? Number(override) - (c.baseline || 0) : 0;
              const inputSlug = c.name.replace(/\W+/g, '-');
              return (
                <div
                  key={c.name}
                  className="px-3 py-2 flex items-center gap-3 text-sm"
                  data-testid={`component-row-${inputSlug}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-slate-800">{c.name}</div>
                    <div className="text-[11px] text-slate-500">
                      Baseline {fmtHours(c.baseline)} · {c.role || 'CF'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => stepValue(c.name, c.baseline, -10)}
                      data-testid={`component-step-down-${inputSlug}`}
                      title="-10 hrs"
                    >
                      −
                    </Button>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={override ?? ''}
                      onChange={(e) => onChange(c.name, e.target.value)}
                      placeholder={String(Math.round(c.baseline || 0))}
                      className="h-8 w-24 text-sm text-right font-medium"
                      data-testid={`component-input-${inputSlug}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => stepValue(c.name, c.baseline, 10)}
                      data-testid={`component-step-up-${inputSlug}`}
                      title="+10 hrs"
                    >
                      +
                    </Button>
                  </div>
                  <div className="w-20 text-right">
                    {hasOverride && delta !== 0 ? (
                      <DeltaPill delta={delta} format="hours" />
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineDrivenControls({ targetWeeks, setTargetWeeks, flexLever, setFlexLever, currentWeeks, loading }) {
  return (
    <div className="space-y-5" data-testid="timeline-driven-controls">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-medium text-slate-700">Target duration (weeks)</Label>
          <span className="text-sm font-semibold text-slate-900" data-testid="target-weeks-display">
            {loading ? '…' : `${targetWeeks} wk`}
          </span>
        </div>
        <Slider
          value={[targetWeeks]}
          onValueChange={(v) => setTargetWeeks(v[0])}
          min={4}
          max={Math.max(Math.round(currentWeeks * 1.6), 80)}
          step={1}
          className="my-3"
          data-testid="target-weeks-slider"
          disabled={loading}
        />
        <div className="flex justify-between text-[11px] text-slate-400">
          <span>4 wk</span>
          <span className="text-slate-600" data-testid="target-weeks-baseline-label">
            {loading ? 'Baseline loading…' : `Baseline ${currentWeeks} wk`}
          </span>
          <span>{Math.max(Math.round(currentWeeks * 1.6), 80)} wk</span>
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium text-slate-700 mb-2 block">Flex lever</Label>
        <div className="grid grid-cols-2 gap-2" data-testid="flex-lever-toggle">
          <button
            type="button"
            onClick={() => setFlexLever('team')}
            className={`border rounded-md px-3 py-2.5 text-left transition ${
              flexLever === 'team'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white hover:border-slate-400'
            }`}
            data-testid="flex-lever-team"
          >
            <div className="flex items-center gap-2 text-xs font-medium">
              <Users className="w-3.5 h-3.5" /> Scale team size
            </div>
            <div className={`text-[11px] mt-1 ${flexLever === 'team' ? 'text-slate-300' : 'text-slate-500'}`}>
              Keep scope fixed · peak FTE rises
            </div>
          </button>
          <button
            type="button"
            onClick={() => setFlexLever('scope')}
            className={`border rounded-md px-3 py-2.5 text-left transition ${
              flexLever === 'scope'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white hover:border-slate-400'
            }`}
            data-testid="flex-lever-scope"
          >
            <div className="flex items-center gap-2 text-xs font-medium">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Scale scope
            </div>
            <div className={`text-[11px] mt-1 ${flexLever === 'scope' ? 'text-slate-300' : 'text-slate-500'}`}>
              Keep team fixed · effort &amp; cost drop
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SimulatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth();

  const [estimates, setEstimates] = useState([]);
  const [estimateId, setEstimateId] = useState(searchParams.get('estimateId') || '');
  const [estimate, setEstimate] = useState(null);
  const simSym = getCurrencySymbol(estimate?.currency_code);
  const [baseline, setBaseline] = useState(null);  // {total_duration_weeks, peak_fte, total_effort, total_cost, ...}
  const [mode, setMode] = useState('effort'); // 'effort' | 'timeline'

  // Effort mode state
  const [overrides, setOverrides] = useState({}); // { componentName: stringValue }

  // Timeline mode state
  const [targetWeeks, setTargetWeeks] = useState(30);
  const [flexLever, setFlexLever] = useState('team');

  // Result
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [lastRequest, setLastRequest] = useState(null);   // last body sent to /simulator/run
  const [saveOpen, setSaveOpen] = useState(false);
  const [scenarioReloadKey, setScenarioReloadKey] = useState(0);

  // Load estimates list
  useEffect(() => {
    axios.get(`${API_URL}/api/estimates`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data.estimates || []);
        setEstimates(list);
        if (!estimateId && list.length) {
          setEstimateId(list[0].id);
        }
      })
      .catch(() => toast.error('Could not load estimates'));
  }, []);  // eslint-disable-line

  // Load selected estimate
  useEffect(() => {
    if (!estimateId) return;
    setBaseline(null);
    setResult(null);
    setOverrides({});
    axios.get(`${API_URL}/api/estimates/${estimateId}`, { withCredentials: true })
      .then((res) => {
        setEstimate(res.data);
      })
      .catch(() => toast.error('Could not load estimate'));

    // Fetch the TRUE planner baseline (the estimate's `results.project_weeks`
    // is the math-model week count and disagrees with the planner duration).
    axios.get(`${API_URL}/api/simulator/baseline/${estimateId}`, { withCredentials: true })
      .then((res) => {
        setBaseline(res.data);
        const bw = res.data?.total_duration_weeks || 30;
        setTargetWeeks(bw);  // Lock slider to baseline on load.
      })
      .catch(() => {
        // Non-fatal — slider will fall back to the estimate's stored weeks.
        setTargetWeeks(30);
      });
  }, [estimateId]);

  const currentWeeks = baseline?.total_duration_weeks
    || estimate?.results?.total_duration_weeks
    || estimate?.results?.project_weeks
    || 30;

  const componentsForEditor = useMemo(() => {
    if (!estimate) return [];
    const breakdown = estimate?.results?.component_breakdown || [];
    return breakdown.map((c) => ({
      name: c.name,
      group: c.group,
      role: c.role,
      baseline: c.final_effort || 0,
    })).filter((c) => c.name);
  }, [estimate]);

  const handleRun = useCallback(async () => {
    if (!estimateId) return;
    setRunning(true);
    setResult(null);
    try {
      const body = { estimate_id: estimateId, mode };
      if (mode === 'effort') {
        const parsed = {};
        Object.entries(overrides).forEach(([name, v]) => {
          if (v === '' || v === undefined || v === null) return;
          const num = Number(v);
          if (!Number.isNaN(num) && num >= 0) parsed[name] = num;
        });
        if (!Object.keys(parsed).length) {
          toast.message('No effort overrides set — enter at least one component hour to run.');
          setRunning(false);
          return;
        }
        body.component_overrides = parsed;
      } else {
        body.target_weeks = targetWeeks;
        body.flex_lever = flexLever;
      }
      const res = await axios.post(`${API_URL}/api/simulator/run`, body, { withCredentials: true });
      setResult(res.data);
      setLastRequest(body);
      toast.success('Scenario simulated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Simulation failed');
    } finally {
      setRunning(false);
    }
  }, [estimateId, mode, overrides, targetWeeks, flexLever]);

  const handleReset = useCallback(() => {
    setOverrides({});
    setResult(null);
  }, []);

  const diff = result?.diff;

  return (
    <div className="w-full px-4 py-6 space-y-5" data-testid="simulator-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="w-5 h-5 text-slate-700" />
            <h1 className="text-2xl font-semibold text-slate-900">Scenario Simulator</h1>
            <Badge variant="secondary" className="text-[10px] font-normal">Bidirectional</Badge>
          </div>
          <p className="text-sm text-slate-600">
            Drive from <strong>effort</strong> (edit component hours) or drive from <strong>timeline</strong> (set a target duration and choose whether to flex the team or the scope).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={estimateId} onValueChange={setEstimateId}>
            <SelectTrigger className="w-[280px]" data-testid="simulator-estimate-select">
              <SelectValue placeholder="Pick an estimate" />
            </SelectTrigger>
            <SelectContent>
              {estimates.slice(0, 50).map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.estimate_name || 'Unnamed'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="simulator-mode-toggle">
        <button
          type="button"
          onClick={() => { setMode('effort'); setResult(null); }}
          className={`px-4 py-1.5 text-sm rounded transition ${
            mode === 'effort' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
          }`}
          data-testid="simulator-mode-effort"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 inline mr-1.5" />
          Drive from effort
        </button>
        <button
          type="button"
          onClick={() => { setMode('timeline'); setResult(null); }}
          className={`px-4 py-1.5 text-sm rounded transition ${
            mode === 'timeline' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
          }`}
          data-testid="simulator-mode-timeline"
        >
          <Target className="w-3.5 h-3.5 inline mr-1.5" />
          Drive from timeline
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Inputs */}
        <Card className="lg:col-span-2" data-testid="simulator-inputs-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{mode === 'effort' ? 'Component effort overrides' : 'Timeline target'}</span>
              <Button
                size="sm"
                onClick={handleRun}
                disabled={running || !estimate}
                data-testid="simulator-run-btn"
                className="h-8"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                {running ? 'Running…' : 'Simulate'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {!estimate ? (
              <div className="text-sm text-slate-400">Pick an estimate to begin.</div>
            ) : mode === 'effort' ? (
              <ComponentEffortEditor
                components={componentsForEditor}
                overrides={overrides}
                onChange={(name, v) => setOverrides((prev) => ({ ...prev, [name]: v }))}
                onReset={handleReset}
              />
            ) : (
              <TimelineDrivenControls
                targetWeeks={targetWeeks}
                setTargetWeeks={setTargetWeeks}
                flexLever={flexLever}
                setFlexLever={setFlexLever}
                currentWeeks={currentWeeks}
                loading={!baseline}
              />
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <div className="lg:col-span-3 space-y-4" data-testid="simulator-results-panel">
          {!result ? (
            <Card className="border-dashed">
              <CardContent className="p-10 text-center">
                <ChevronRight className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-600 font-medium">
                  {mode === 'effort'
                    ? 'Enter overrides on the left and hit Simulate to see deltas.'
                    : 'Move the slider, pick a flex lever, and hit Simulate.'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Baseline: {currentWeeks} wk · {fmtHours(baseline?.total_effort ?? estimate?.results?.total_effort)} · {fmtCurrency(baseline?.total_cost ?? estimate?.results?.total_cost, simSym)}
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* KPI deltas */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="simulator-kpi-grid">
                <KPICard icon={Clock} label="Effort" baseline={diff.effort.original} whatif={diff.effort.whatif} format="hours" testid="kpi-effort" />
                <KPICard icon={DollarSign} label="Cost" baseline={diff.cost.original} whatif={diff.cost.whatif} format="currency" testid="kpi-cost" sym={simSym} />
                <KPICard icon={Target} label="Duration" baseline={diff.duration.original} whatif={diff.duration.whatif} format="weeks" invert testid="kpi-duration" />
                <KPICard icon={Users} label="Peak FTE" baseline={diff.peak_fte.original} whatif={diff.peak_fte.whatif} format="fte" invert testid="kpi-peak-fte" />
              </div>

              {/* Applied context */}
              {result.applied && (
                <Card data-testid="simulator-applied-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Applied scenario</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 text-xs text-slate-700 space-y-1">
                    <div><span className="text-slate-500">Mode:</span> <span className="font-medium capitalize">{result.applied.mode}</span></div>
                    {result.applied.flex_lever && (
                      <div>
                        <span className="text-slate-500">Flex lever:</span>{' '}
                        <span className="font-medium capitalize">{result.applied.flex_lever}</span>
                        {result.applied.scale_factor != null && (
                          <span className="text-slate-500 ml-2">(scale × {result.applied.scale_factor})</span>
                        )}
                      </div>
                    )}
                    {result.applied.target_weeks != null && (
                      <div>
                        <span className="text-slate-500">Target:</span>{' '}
                        <span className="font-medium">{result.applied.target_weeks} wk</span>
                        {result.applied.current_weeks != null && (
                          <span className="text-slate-500 ml-2">(from {result.applied.current_weeks} wk)</span>
                        )}
                      </div>
                    )}
                    {result.applied.component_overrides && Object.keys(result.applied.component_overrides).length > 0 && (
                      <div>
                        <span className="text-slate-500">Overrides applied:</span>{' '}
                        <span className="font-medium">{Object.keys(result.applied.component_overrides).length} components</span>
                      </div>
                    )}
                    {result.applied.role_multipliers && Object.keys(result.applied.role_multipliers).length > 0 && (
                      <div data-testid="applied-role-multipliers">
                        <div className="text-slate-500 mb-1 mt-1">Management-overhead tax (PM/CS/CF track the calendar; DE/IN/DM/IF stay constant):</div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(result.applied.role_multipliers).map(([role, mult]) => {
                            const m = Number(mult) || 1;
                            const delta = m - 1;
                            const color = m === 1
                              ? 'bg-slate-100 text-slate-500'
                              : (m > 1 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800');
                            return (
                              <span
                                key={role}
                                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${color}`}
                                data-testid={`role-mult-${role}`}
                                title={`${role}: ×${m.toFixed(3)}`}
                              >
                                {role} ×{m.toFixed(2)}
                                {delta !== 0 && (
                                  <span className="ml-1 opacity-70">({delta > 0 ? '+' : ''}{(delta * 100).toFixed(1)}%)</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Component diff table */}
              {result.component_diff && result.component_diff.length > 0 && (
                <Card data-testid="simulator-component-diff-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Component effort shift</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[380px] overflow-auto">
                      <table className="w-full text-xs" data-testid="simulator-component-diff-table">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-slate-600">
                            <th className="text-left px-3 py-2 font-medium">Component</th>
                            <th className="text-right px-3 py-2 font-medium">Baseline</th>
                            <th className="text-right px-3 py-2 font-medium">Scenario</th>
                            <th className="text-right px-3 py-2 font-medium">Δ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {result.component_diff
                            .filter((c) => c.delta !== 0 || c.original_effort || c.whatif_effort)
                            .slice()
                            .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
                            .map((c) => (
                              <tr key={c.name}>
                                <td className="px-3 py-1.5 text-slate-800 truncate max-w-[260px]">{c.name}</td>
                                <td className="px-3 py-1.5 text-right text-slate-600">{fmtHours(c.original_effort)}</td>
                                <td className="px-3 py-1.5 text-right text-slate-900 font-medium">{fmtHours(c.whatif_effort)}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <DeltaPill delta={c.delta} format="hours" />
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick-links back into the app */}
              <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => setSaveOpen(true)}
                  data-testid="save-scenario-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Bookmark className="w-3.5 h-3.5 mr-1.5" />
                  Save as scenario
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/compare?estimateId=${estimateId}`)}
                  data-testid="simulator-open-compare"
                >
                  Open in Compare
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/planner?estimateId=${estimateId}`)}
                  data-testid="simulator-open-planner"
                >
                  Open in Planner
                </Button>
              </div>
            </>
          )}
          {/* Saved scenarios for this estimate (if any) */}
          {estimateId && (
            <SavedScenariosCard
              estimateId={estimateId}
              reloadKey={scenarioReloadKey}
              onOpenCompare={(scenarioId, sourceId) => navigate(`/compare?estimateId=${sourceId}&scenarioId=${scenarioId}`)}
              onCompareAll={(ids) => navigate(`/compare?ids=${ids.join(',')}`)}
            />
          )}
        </div>
      </div>

      {/* Save scenario dialog */}
      <SaveScenarioDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        sourceEstimateName={estimate?.estimate_name}
        simulatorRequest={lastRequest}
        onSaved={() => setScenarioReloadKey((k) => k + 1)}
      />
    </div>
  );
}
